/**
 * nbn-trustpilot.js
 * Nine by Nine Golf — live Trustpilot review-count for the custom cart badge.
 *
 * The badge itself is a static SVG (order-summary card in nbn-main-cart.liquid).
 * This script optionally upgrades the "Based on X reviews" number to the live
 * value from Trustpilot, using the SAME browser-friendly TrustBox data endpoint
 * the dedicated Trustpilot carousel section already uses:
 *
 *   https://widget.trustpilot.com/trustbox-data/{TEMPLATE_ID}
 *       ?businessUnitId={BUSINESS_UNIT_ID}&locale=en-GB...
 *
 * Unlike the key-based api.trustpilot.com endpoint, this one is CORS-enabled
 * for browsers and needs no API key — only the Business Unit ID and a Template
 * ID (both from the section settings). The response includes:
 *   data.businessUnit.numberOfReviews.total
 *   data.businessUnit.trustScore
 *
 * If the credentials are missing or the request fails, the badge keeps the
 * section-setting fallback count that Liquid already rendered.
 */

(function () {
  'use strict';

  // Default TrustBox template used purely as a data source for the count.
  // (Any valid template returns the same businessUnit summary block.)
  var DEFAULT_TEMPLATE_ID = '5419b6a8b0d04a076446a9ad';

  function formatCount(n) {
    try {
      return Number(n).toLocaleString();
    } catch (e) {
      return String(n);
    }
  }

  // trustScore (e.g. 4.8) → whole star count, matching the section carousel.
  function scoreToStarCount(score) {
    if (score >= 4.5) return 5;
    if (score >= 3.5) return 4;
    if (score >= 2.5) return 3;
    if (score >= 1.5) return 2;
    return 1;
  }

  // trustScore → Trustpilot rating label.
  function scoreToLabel(score) {
    if (score >= 4.5) return 'Excellent';
    if (score >= 3.5) return 'Great';
    if (score >= 2.5) return 'Average';
    if (score >= 1.5) return 'Poor';
    return 'Bad';
  }

  function updateBadge(badge) {
    var businessUnitId = badge.getAttribute('data-tp-businessunit');
    var templateId = badge.getAttribute('data-tp-template') || DEFAULT_TEMPLATE_ID;
    var countEl = badge.querySelector('[data-tp-count]');

    if (!businessUnitId || !countEl) return; // nothing to fetch / update

    var url =
      'https://widget.trustpilot.com/trustbox-data/' +
      encodeURIComponent(templateId) +
      '?businessUnitId=' + encodeURIComponent(businessUnitId) +
      '&locale=en-GB&includeReviews=0' +
      '&random=' + Math.random().toString(36).slice(2);

    fetch(url, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('TrustBox data ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var bu = data && data.businessUnit;
        if (!bu) return; // keep fallback

        var total =
          bu.numberOfReviews && typeof bu.numberOfReviews.total !== 'undefined'
            ? bu.numberOfReviews.total
            : null;

        if (total !== null) {
          countEl.textContent = formatCount(total) + ' reviews';
          badge.setAttribute(
            'aria-label',
            'Trustpilot — Excellent rating, based on ' + formatCount(total) + ' reviews'
          );
        }

        // Also sync the rating label + stars if the score is available and the
        // badge exposes those elements (kept in sync with the live score).
        var score = bu.trustScore;
        if (score) {
          var labelEl = badge.querySelector('.nbn-tp-excellent');
          if (labelEl) labelEl.textContent = scoreToLabel(score);
          // (Star SVG is left static at 5 — the badge is the "Excellent" state.
          //  scoreToStarCount is available if a dynamic star fill is wanted.)
          void scoreToStarCount;
        }
      })
      .catch(function (err) {
        if (window.console && console.debug) {
          console.debug('[NBN Trustpilot] live count unavailable:', err.message);
        }
      });
  }

  function boot() {
    var badges = document.querySelectorAll('.nbn-tp-badge[data-tp-businessunit]');
    if (!badges.length) return;
    badges.forEach(updateBadge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();